import React, {
  FC,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ChangeEvent,
} from 'react';
import { observer } from 'mobx-react';
import Text from 'components/UI/Text';
import { useTranslation } from 'react-i18next';
import { ActionMode } from 'models/shared';
import styled from 'styled-components';
import { GrowerSystem } from 'models/grower';
import Select from 'components/UI/Select';
import Input from 'components/UI/Input';
import { ActionParams } from 'models/actions';
import { Checkbox } from 'primereact/checkbox';
import Grid from 'components/UI/Grid';
import { DropdownChangeEvent } from 'primereact/dropdown';
import {
  modesOptions,
  topModes,
  staticModes,
  staticWithValueModes,
} from 'utils/actionsUtills';

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
  text-align: start;
`;

const StyledGrid = styled(Grid)`
  justify-content: unset;
`;

interface ActionManagementDialogFormProps {
  params: ActionParams;
  serials: {
    label: string;
    value: string;
  }[];
  handleChangeParams: (params: ActionParams) => void;
  eligibleSystems: GrowerSystem[];
  mode: ActionMode;
}

const checkIfModeIsStatic = (mode: string) => {
  return mode === 'mode_Stop' || mode === 'mode_Virtual';
}

const checkIfModeIsStaticWithValue = (mode: string) => {
  return mode === 'mode_DryContact';
}

const checkIfModeIsDynamic = (mode: string) => {
  return mode === 'mode_Volume' || mode === 'mode_MM' || mode === 'mode_Time';
}

const ActionManagementDialogForm: FC<ActionManagementDialogFormProps> =
  observer(({ params, serials, handleChangeParams, eligibleSystems, mode }) => {
    const { t: growerT } = useTranslation('grower');
    const [currentMode, setCurrentMode] = useState<string | null>(null);
    const currentModeOptions = useMemo(() => {
      return modesOptions[currentMode as keyof typeof modesOptions];
    }, [currentMode]);

    const currentParamMode = useMemo(() => {
      if (params.mode) {
        const paramModeValues = Object.keys(params.mode);
        const foundMode = topModes.find((modeObject) =>
          paramModeValues.includes(modeObject.value),
        );

        return foundMode;
      }

      return null;
    }, [params.mode]);

    const modeIsStatic = useMemo(() => {
      return currentMode === 'mode_Stop' || currentMode === 'mode_Virtual';
    }, [currentMode]);

    const modeIsStaticWithValue = useMemo(() => {
      return currentMode === 'mode_DryContact';
    }, [currentMode]);

    const modeIsDynamic = useMemo(() => {
      return (
        currentMode === 'mode_Volume' ||
        currentMode === 'mode_MM' ||
        currentMode === 'mode_Time'
      );
    }, [currentMode]);

    const currentParamModeValue = useMemo(() => {
      if (params.mode) {
        return params?.mode?.value || '';
      }

      return '';
    }, [params.mode]);

    const handleChangeMode = useCallback(
      (e: DropdownChangeEvent) => {
        const foundMode = topModes.find((modeObject) => modeObject.value === e.value);
        const modeLabel = foundMode?.label;

        if (checkIfModeIsStatic(e.value)) {
          handleChangeParams({
            ...params,
            value: 0,
            mode: { [e.value]: staticModes[e.value as keyof typeof staticModes] },
          });
        }

        if (checkIfModeIsStaticWithValue(e.value)) {
          handleChangeParams({
            ...params,
            value: 0,
            mode: { [e.value]: staticWithValueModes[e.value as keyof typeof staticWithValueModes] },
          });
        }

        if (checkIfModeIsDynamic(e.value)) {
          handleChangeParams({
            ...params,
            value: 0,
            mode: {
              [e.value]: undefined,
            }
          });
        }

        setCurrentMode(e.value);
      },
      [currentMode, handleChangeParams, params],
    );

    const handleChangeUnit = useCallback(
      (e: DropdownChangeEvent) => {
        handleChangeParams({
          ...params,
          mode: { ...params.mode, [currentMode as string]: e.value },
        });
      },
      [currentMode, handleChangeParams, params],
    );

    const handleChangeValueSatic = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        handleChangeParams({
          ...params,
          mode: { ...params.mode, value: Number(e.target.value) },
        });
      },
      [handleChangeParams, params],
    );

    useEffect(() => {
      if (mode === ActionMode.EDIT && params.mode && !currentMode) {
        const paramModeValues = Object.keys(params.mode);
        const foundMode = topModes.find((modeObject) =>
          paramModeValues.includes(modeObject.value),
        );

        setCurrentMode(foundMode?.value || null);
      }
    }, [currentMode, mode, params.mode]);

    return (
      <>
        <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
          <NoWrappingText>{growerT('actions_form_name')}:</NoWrappingText>
          <Input
            label="Name"
            value={params.name}
            onChange={(e) =>
              handleChangeParams({ ...params, name: e.target.value })
            }
          />
        </StyledGrid>
        <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
          <NoWrappingText>{growerT('actions_form_serial')}:</NoWrappingText>
          <Select
            placeholder="Select a serial..."
            options={serials}
            value={params.serial}
            onChange={(e) => handleChangeParams({ ...params, serial: e.value })}
          />
        </StyledGrid>
        <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
          <NoWrappingText>{growerT('actions_form_mode')}:</NoWrappingText>
          <Select
            options={topModes}
            placeholder="Select a mode..."
            value={currentMode || null}
            onChange={handleChangeMode}
          />
        </StyledGrid>
        {/*
        Units
        */}
        {modeIsDynamic && (
          <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
            <NoWrappingText>{growerT('actions_form_units')}:</NoWrappingText>
            <Select
              options={currentModeOptions}
              placeholder="Select a unit..."
              value={params?.mode?.[currentMode as string] || null}
              onChange={handleChangeUnit}
            />
          </StyledGrid>
        )}
        {modeIsStaticWithValue && (
          <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
            <NoWrappingText>{growerT('actions_form_units')}:</NoWrappingText>
            <Select
              options={currentModeOptions}
              placeholder="Select a unit..."
              value={currentModeOptions?.[0]?.value}
              disabled
            // onChange={handleChangeUnit}
            />
          </StyledGrid>
        )}
        {modeIsDynamic && params?.mode?.[currentMode as string] && (
          <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
            <NoWrappingText>{growerT('actions_form_value')}:</NoWrappingText>
            <Input
              label="Value"
              value={currentParamModeValue}
              type="number"
              onChange={(e) =>
                handleChangeParams({
                  ...params,
                  mode: { ...params.mode, value: Number(e.target.value) },
                })
              }
            />
          </StyledGrid>
        )}
        {modeIsStatic && (
          <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
            <NoWrappingText>{growerT('actions_form_value')}:</NoWrappingText>
            <Input label="NA" disabled placeholder="NA" />
          </StyledGrid>
        )}
        {modeIsStaticWithValue && (
          <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
            <NoWrappingText>{growerT('actions_form_value')}:</NoWrappingText>
            <Input
              label="Value"
              value={params?.mode?.value}
              type="number"
              onChange={handleChangeValueSatic}
            />
          </StyledGrid>
        )}
        <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
          <Text>Notify on activation:</Text>
          <Checkbox
            title="Notify on activation"
            checked={params.notifyOnTrigger || false}
            onChange={(e) =>
              handleChangeParams({
                ...params,
                notifyOnTrigger: e.target.checked,
              })
            }
          />
        </StyledGrid>
      </>
    );
  });

export default ActionManagementDialogForm;
